/**
 * Where the form view's parts go for each layout (Option C, C1, plan §3.5 / §3.6):
 *
 * - Two panes (create, viewport ≥ 936): a grid with the rail (220 px incl. its border) left and the pane right. The rail's
 *   entry points come AFTER the pane in the DOM (Tab order: rail → form → footer → entry points) and sit at the rail's
 *   bottom through grid areas. In the short tier the rail scrolls as a whole, entry points included.
 * - One column (create, below 936): the compact bar above the pane.
 * - Edit mode: the pane alone (no start view, no rail: the engine is locked, D1).
 * - Very-short tier (measured): only the dialog header and the footer stay fixed; the compact bar, pane header, result
 *   summary and form share one scroll region.
 *
 * The pane is a `<section>` named by its header (`connection-form-pane`, `data-engine`).
 *
 * C1b (the start ↔ rail transition): the layout root is the view's page (`data-flip-page`: reduced motion cross-fades
 * it, a narrow window slides it); the pane slides in after the rail items start (`data-flip-chrome="pane"`); the
 * rail's tint is one layer under both rail cells, so it can fade in while the items fly inside them; during a flight
 * the rail cells stack above the pane (`data-flip-lift`) and the rail shows items arriving from outside its box
 * (`data-flip-scroller`, opened by the transition when nothing in it scrolls; `src/index.css`).
 */
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type { DatabaseEngine } from '../../components/connectionFormSchema';
import type { DialogLayout } from '../hooks/useDialogLayout';
import { PANE_TITLE_ID } from './ConnectionPaneHeader';

type Props = {
  layout: DialogLayout;
  isEditMode: boolean;
  engine: DatabaseEngine;
  rail: ReactNode;
  railEntries: ReactNode;
  compactBar: ReactNode;
  paneHeader: ReactNode;
  /** The Test answer: the full strip, or the short tier's one-line summary. */
  results: ReactNode;
  /** The form's scroll region content (Details, the paste field, the form). */
  scrollContent: ReactNode;
  notices: ReactNode;
  footer: ReactNode;
};

export function ConnectionDialogShell({ layout, isEditMode, engine, rail, railEntries, compactBar, paneHeader, results, scrollContent, notices, footer }: Props) {
  const twoPane = !isEditMode && layout.columns === 'two-pane';
  const compact = !isEditMode && !twoPane;
  const scroller = cn('min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]');
  const pane = (
    <section
      aria-labelledby={PANE_TITLE_ID}
      data-testid="connection-form-pane"
      data-engine={engine}
      data-flip-chrome="pane"
      className="flex min-h-0 min-w-0 flex-1 flex-col [grid-area:pane]"
    >
      {layout.veryShort ? (
        <div className={scroller} data-testid="connection-form-scroll">
          {compact && compactBar}
          {paneHeader}
          {results}
          <div className="px-6 pb-4 pt-1">{scrollContent}</div>
        </div>
      ) : (
        <>
          {paneHeader}
          <div className={cn(scroller, 'px-6 pb-4 pt-1', layout.short ? 'min-h-24' : 'min-h-[10rem]')} data-testid="connection-form-scroll">
            {scrollContent}
          </div>
          {results}
        </>
      )}
      {notices}
      {footer}
    </section>
  );

  if (twoPane) {
    return (
      <div
        className={cn(
          'grid min-h-0 flex-1 grid-cols-[220px_minmax(0,1fr)] grid-rows-[minmax(0,1fr)_auto]',
          layout.short ? "[grid-template-areas:'rail_pane'_'rail_pane']" : "[grid-template-areas:'rail_pane'_'entries_pane']",
        )}
        data-layout="two-pane"
        data-flip-page
      >
        <div aria-hidden data-flip-chrome="fade" className="border-r bg-muted/40 [grid-column:1] [grid-row:1/-1]" />
        <div data-flip-lift data-flip-scroller className="min-h-0 overflow-y-auto [grid-area:rail]">
          {rail}
          {layout.short && railEntries}
        </div>
        {pane}
        {!layout.short && (
          <div data-flip-lift className="[grid-area:entries]">
            {railEntries}
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="flex min-h-0 flex-1 flex-col" data-layout={compact ? 'one-column' : 'edit'} data-flip-page>
      {compact && !layout.veryShort && compactBar}
      {pane}
    </div>
  );
}
